/**
 * Phase 3F: Governance Controls Hook
 * Manages feature flags, opt-ins, and governance status
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type {
  FeatureFlag,
  ClientOptIn,
  SurgeonOptIn,
  GovernanceStatus,
  Phase3FeatureFlag,
} from '@/lib/governance/types';
import {
  PHASE3_ACKNOWLEDGMENT_TEXT,
} from '@/lib/governance/types';
import {
  logFeatureFlagChanged,
  logClientOptInChange,
  logSurgeonOptInChange,
  logDisclaimerAccepted,
} from '@/lib/governance/auditLogger';

interface UsePhase3GovernanceReturn {
  // Status
  governanceStatus: GovernanceStatus | null;
  isLoading: boolean;
  error: string | null;
  
  // Feature flags (admin only)
  featureFlags: FeatureFlag[];
  toggleFeatureFlag: (flagName: Phase3FeatureFlag, enabled: boolean) => Promise<void>;
  
  // Client opt-in (admin only)
  clientOptIn: ClientOptIn | null;
  setClientOptIn: (optedIn: boolean) => Promise<void>;
  
  // Surgeon opt-in
  surgeonOptIn: SurgeonOptIn | null;
  setSurgeonOptIn: (optedIn: boolean, acceptDisclaimer: boolean) => Promise<void>;
  
  // Helpers
  isPhase3Enabled: boolean;
  isFeatureEnabled: (feature: Phase3FeatureFlag) => boolean;
  refresh: () => Promise<void>;
}

export function usePhase3Governance(
  userId: string | null,
  clientId: string | null,
  userRole: string | null
): UsePhase3GovernanceReturn {
  const { isDemoMode } = useDemoMode();
  const { toast } = useToast();
  
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [clientOptIn, setClientOptInState] = useState<ClientOptIn | null>(null);
  const [surgeonOptIn, setSurgeonOptInState] = useState<SurgeonOptIn | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all governance data
   */
  const fetchGovernanceData = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (isDemoMode) {
        // Demo mode: simulate governance data
        setFeatureFlags([
          { id: '1', flagName: 'phase_3_enabled', isEnabled: true, description: 'Master toggle', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { id: '2', flagName: 'pattern_analysis_enabled', isEnabled: true, description: 'Pattern analysis', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { id: '3', flagName: 'revenue_flags_enabled', isEnabled: true, description: 'Revenue flags', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { id: '4', flagName: 'subset_insights_enabled', isEnabled: true, description: 'Subset insights', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { id: '5', flagName: 'financial_variance_enabled', isEnabled: true, description: 'Financial variance', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        ]);
        setClientOptInState({
          id: 'demo-client-optin',
          clientId: clientId || 'demo-client',
          optedIn: true,
          optedInAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setSurgeonOptInState({
          id: 'demo-surgeon-optin',
          surgeonId: userId,
          clientId: clientId || undefined,
          optedIn: true,
          optedInAt: new Date().toISOString(),
          disclaimerAccepted: true,
          disclaimerAcceptedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      // Fetch feature flags
      const { data: flagsData, error: flagsError } = await supabase
        .from('phase3_feature_flags')
        .select('*');
      
      if (flagsError) throw flagsError;
      
      setFeatureFlags((flagsData || []).map(f => ({
        id: f.id,
        flagName: f.flag_name as Phase3FeatureFlag,
        isEnabled: f.is_enabled,
        description: f.description || '',
        createdAt: f.created_at,
        updatedAt: f.updated_at,
        updatedBy: f.updated_by || undefined,
      })));

      // Fetch client opt-in if we have a client ID
      if (clientId) {
        const { data: clientData } = await supabase
          .from('phase3_client_optins')
          .select('*')
          .eq('client_id', clientId)
          .maybeSingle();
        
        if (clientData) {
          setClientOptInState({
            id: clientData.id,
            clientId: clientData.client_id,
            optedIn: clientData.opted_in,
            optedInAt: clientData.opted_in_at || undefined,
            optedInBy: clientData.opted_in_by || undefined,
            optedOutAt: clientData.opted_out_at || undefined,
            optedOutBy: clientData.opted_out_by || undefined,
            acknowledgmentText: clientData.acknowledgment_text || undefined,
            createdAt: clientData.created_at,
            updatedAt: clientData.updated_at,
          });
        } else {
          setClientOptInState(null);
        }
      }

      // Fetch surgeon opt-in if user is a surgeon
      if (userRole === 'surgeon') {
        const { data: surgeonData } = await supabase
          .from('phase3_surgeon_optins')
          .select('*')
          .eq('surgeon_id', userId)
          .maybeSingle();
        
        if (surgeonData) {
          setSurgeonOptInState({
            id: surgeonData.id,
            surgeonId: surgeonData.surgeon_id,
            clientId: surgeonData.client_id || undefined,
            optedIn: surgeonData.opted_in,
            optedInAt: surgeonData.opted_in_at || undefined,
            acknowledgmentText: surgeonData.acknowledgment_text || undefined,
            disclaimerAccepted: surgeonData.disclaimer_accepted,
            disclaimerAcceptedAt: surgeonData.disclaimer_accepted_at || undefined,
            createdAt: surgeonData.created_at,
            updatedAt: surgeonData.updated_at,
          });
        } else {
          setSurgeonOptInState(null);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch governance data';
      setError(message);
      console.error('Governance fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, clientId, userRole, isDemoMode]);

  /**
   * Toggle a feature flag (admin only)
   */
  const toggleFeatureFlag = useCallback(async (flagName: Phase3FeatureFlag, enabled: boolean) => {
    if (!userId || isDemoMode) {
      // Demo mode: just update local state
      setFeatureFlags(prev => prev.map(f => 
        f.flagName === flagName ? { ...f, isEnabled: enabled } : f
      ));
      toast({
        title: enabled ? 'Feature Enabled' : 'Feature Disabled',
        description: `${flagName.replace(/_/g, ' ')} has been ${enabled ? 'enabled' : 'disabled'}.`,
      });
      return;
    }

    const currentFlag = featureFlags.find(f => f.flagName === flagName);
    const previousValue = currentFlag?.isEnabled ?? false;

    try {
      const { error: updateError } = await supabase
        .from('phase3_feature_flags')
        .update({ 
          is_enabled: enabled,
          updated_by: userId,
        })
        .eq('flag_name', flagName);

      if (updateError) throw updateError;

      // Log the change
      await logFeatureFlagChanged(userId, userRole || 'unknown', flagName, previousValue, enabled);

      // Update local state
      setFeatureFlags(prev => prev.map(f => 
        f.flagName === flagName ? { ...f, isEnabled: enabled, updatedBy: userId } : f
      ));

      toast({
        title: enabled ? 'Feature Enabled' : 'Feature Disabled',
        description: `${flagName.replace(/_/g, ' ')} has been ${enabled ? 'enabled' : 'disabled'}.`,
      });
    } catch (err) {
      console.error('Failed to toggle feature flag:', err);
      toast({
        title: 'Error',
        description: 'Failed to update feature flag',
        variant: 'destructive',
      });
    }
  }, [userId, userRole, featureFlags, isDemoMode, toast]);

  /**
   * Set client opt-in status (admin only)
   */
  const setClientOptIn = useCallback(async (optedIn: boolean) => {
    if (!userId || !clientId) return;

    if (isDemoMode) {
      setClientOptInState(prev => prev ? { ...prev, optedIn } : {
        id: 'demo',
        clientId,
        optedIn,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast({
        title: optedIn ? 'Hospital Opted In' : 'Hospital Opted Out',
        description: `Phase 3 analytics have been ${optedIn ? 'enabled' : 'disabled'} for this hospital.`,
      });
      return;
    }

    try {
      const now = new Date().toISOString();
      const { error: upsertError } = await supabase
        .from('phase3_client_optins')
        .upsert({
          client_id: clientId,
          opted_in: optedIn,
          opted_in_at: optedIn ? now : null,
          opted_in_by: optedIn ? userId : null,
          opted_out_at: optedIn ? null : now,
          opted_out_by: optedIn ? null : userId,
          acknowledgment_text: optedIn ? PHASE3_ACKNOWLEDGMENT_TEXT : null,
        }, { onConflict: 'client_id' });

      if (upsertError) throw upsertError;

      // Log the change
      await logClientOptInChange(userId, userRole || 'unknown', clientId, optedIn, 
        optedIn ? PHASE3_ACKNOWLEDGMENT_TEXT : undefined);

      // Refresh data
      await fetchGovernanceData();

      toast({
        title: optedIn ? 'Hospital Opted In' : 'Hospital Opted Out',
        description: `Phase 3 analytics have been ${optedIn ? 'enabled' : 'disabled'} for this hospital.`,
      });
    } catch (err) {
      console.error('Failed to update client opt-in:', err);
      toast({
        title: 'Error',
        description: 'Failed to update hospital opt-in status',
        variant: 'destructive',
      });
    }
  }, [userId, clientId, userRole, isDemoMode, fetchGovernanceData, toast]);

  /**
   * Set surgeon opt-in status
   */
  const setSurgeonOptIn = useCallback(async (optedIn: boolean, acceptDisclaimer: boolean) => {
    if (!userId) return;

    if (isDemoMode) {
      setSurgeonOptInState(prev => prev ? { 
        ...prev, 
        optedIn,
        disclaimerAccepted: acceptDisclaimer,
      } : {
        id: 'demo',
        surgeonId: userId,
        optedIn,
        disclaimerAccepted: acceptDisclaimer,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast({
        title: optedIn ? 'Opted In' : 'Opted Out',
        description: `You have ${optedIn ? 'enabled' : 'disabled'} Phase 3 analytics insights.`,
      });
      return;
    }

    try {
      const now = new Date().toISOString();
      const { error: upsertError } = await supabase
        .from('phase3_surgeon_optins')
        .upsert({
          surgeon_id: userId,
          client_id: clientId,
          opted_in: optedIn,
          opted_in_at: optedIn ? now : null,
          acknowledgment_text: optedIn ? PHASE3_ACKNOWLEDGMENT_TEXT : null,
          disclaimer_accepted: acceptDisclaimer,
          disclaimer_accepted_at: acceptDisclaimer ? now : null,
        }, { onConflict: 'surgeon_id,client_id' });

      if (upsertError) throw upsertError;

      // Log the changes
      await logSurgeonOptInChange(userId, userId, optedIn, clientId || undefined,
        optedIn ? PHASE3_ACKNOWLEDGMENT_TEXT : undefined);
      
      if (acceptDisclaimer) {
        await logDisclaimerAccepted(userId, clientId || undefined);
      }

      // Refresh data
      await fetchGovernanceData();

      toast({
        title: optedIn ? 'Opted In' : 'Opted Out',
        description: `You have ${optedIn ? 'enabled' : 'disabled'} Phase 3 analytics insights.`,
      });
    } catch (err) {
      console.error('Failed to update surgeon opt-in:', err);
      toast({
        title: 'Error',
        description: 'Failed to update opt-in status',
        variant: 'destructive',
      });
    }
  }, [userId, clientId, isDemoMode, fetchGovernanceData, toast]);

  // Computed governance status
  const governanceStatus = useMemo((): GovernanceStatus | null => {
    if (isLoading) return null;

    const masterEnabled = featureFlags.find(f => f.flagName === 'phase_3_enabled')?.isEnabled ?? false;
    const clientOptedIn = clientOptIn?.optedIn ?? false;
    const surgeonOptedIn = surgeonOptIn?.optedIn ?? false;
    const disclaimerAccepted = surgeonOptIn?.disclaimerAccepted ?? false;

    // Effective access requires: master enabled + client opt-in + (surgeon opt-in if surgeon)
    const isSurgeon = userRole === 'surgeon';
    const effectiveAccess = masterEnabled && clientOptedIn && (!isSurgeon || (surgeonOptedIn && disclaimerAccepted));

    const flagsMap: Record<Phase3FeatureFlag, boolean> = {
      phase_3_enabled: false,
      pattern_analysis_enabled: false,
      revenue_flags_enabled: false,
      subset_insights_enabled: false,
      financial_variance_enabled: false,
    };
    featureFlags.forEach(f => {
      flagsMap[f.flagName] = f.isEnabled;
    });

    return {
      masterEnabled,
      clientOptedIn,
      surgeonOptedIn,
      disclaimerAccepted,
      effectiveAccess,
      featureFlags: flagsMap,
    };
  }, [featureFlags, clientOptIn, surgeonOptIn, userRole, isLoading]);

  const isPhase3Enabled = governanceStatus?.effectiveAccess ?? false;

  const isFeatureEnabled = useCallback((feature: Phase3FeatureFlag): boolean => {
    if (!governanceStatus?.effectiveAccess) return false;
    return governanceStatus.featureFlags[feature] ?? false;
  }, [governanceStatus]);

  // Fetch on mount
  useEffect(() => {
    fetchGovernanceData();
  }, [fetchGovernanceData]);

  return {
    governanceStatus,
    isLoading,
    error,
    featureFlags,
    toggleFeatureFlag,
    clientOptIn,
    setClientOptIn,
    surgeonOptIn,
    setSurgeonOptIn,
    isPhase3Enabled,
    isFeatureEnabled,
    refresh: fetchGovernanceData,
  };
}
